import React, { useState } from "react";
import Handlebars from "handlebars";
import {
  Input,
  Select,
  Button,
  InputNumber,
  Modal,
  Tag,
  Alert,
  Spin,
} from "antd";
import {
  DeleteFilled,
  PlusCircleFilled,
  CloseCircleFilled,
  EyeOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import Typography from "app/shared/Typography";

export type FollowupType = "GST_TRANSMISSION" | "INVOICE_FOLLOWUP" | null;

export interface FollowupEntry {
  id: number;
  name: string;
  interval: string;
  time: string;
  intervalAgainst: string;
  templateId: string;
  subject: string;
  logoUrls: string[];
}

export interface EmailFollowupData {
  entries: FollowupEntry[];
  bulkEmailKey: string;
  attachmentKeys: string[];
  attachmentKeyLabels: Record<string, string>;
  fromName: string;
  from: string;
  cc: string;
}

interface Props {
  followupType: FollowupType;
  followupData: EmailFollowupData;
  setFollowupData: (data: EmailFollowupData) => void;
  fileColumns: string[];
  disabled?: boolean;
}

const INTERVAL_AGAINST_OPTIONS = [
  { value: "BOOKING_HOTEL", label: "BOOKING_HOTEL" },
  { value: "TO_TRAVELLER_PRIOR_CHECKIN", label: "TO_TRAVELLER_PRIOR_CHECKIN" },
  {
    value: "TO_TRAVELLER_ON_CHECKIN_DAY",
    label: "TO_TRAVELLER_ON_CHECKIN_DAY",
  },
  { value: "CHECKIN_HOTEL", label: "CHECKIN_HOTEL" },
  {
    value: "TO_TRAVELLER_BEFORE_CHECKOUT",
    label: "TO_TRAVELLER_BEFORE_CHECKOUT",
  },
  { value: "BEFORE_CHECKOUT_HOTEL", label: "BEFORE_CHECKOUT_HOTEL" },
  { value: "AFTER_CHECKOUT_HOTEL", label: "AFTER_CHECKOUT_HOTEL" },
];

const INTERVAL_TO_NAME: Record<string, string> = {
  BOOKING_HOTEL: "On Booking Received",
  TO_TRAVELLER_PRIOR_CHECKIN: "Reminder To Traveller 1 day before checkin",
  TO_TRAVELLER_ON_CHECKIN_DAY: "Reminder To Traveller on day of checkin",
  CHECKIN_HOTEL: "Reminder To Hotel On Checkin",
  TO_TRAVELLER_BEFORE_CHECKOUT: "Reminder To Traveller Before Checkout",
  BEFORE_CHECKOUT_HOTEL: "Reminder To Hotel Before Checkout",
  AFTER_CHECKOUT_HOTEL: "Reminder To Hotel After Checkout",
};

const INTERVAL_TO_TEMPLATE_ID: Record<string, string> = {
  BOOKING_HOTEL: "d-7256716cbab74e059a44797b6b2b7049",
  TO_TRAVELLER_PRIOR_CHECKIN: "d-6fc84038e5cd47c083b643b1659d69b3",
  TO_TRAVELLER_ON_CHECKIN_DAY: "d-6fc84038e5cd47c083b643b1659d69b3",
  CHECKIN_HOTEL: "d-7256716cbab74e059a44797b6b2b7049",
  TO_TRAVELLER_BEFORE_CHECKOUT: "d-6fc84038e5cd47c083b643b1659d69b3",
  BEFORE_CHECKOUT_HOTEL: "d-7256716cbab74e059a44797b6b2b7049",
  AFTER_CHECKOUT_HOTEL: "d-7256716cbab74e059a44797b6b2b7049",
};

const GST_SUBJECT = "Urgent: GST Details for generating invoice for booking ID - {{email_bookingId}}";

const INVOICE_FOLLOWUP_TEMPLATE_ID = "d-a15d95284cb346888a799afb0976e302";
const INVOICE_SUBJECT = "Urgent: Please Re-Share Invoices Issued By {{email_hotel_name}} for {{company_name}}";

const emptyEntry = (id: number, type: FollowupType, interval: string = "0"): FollowupEntry => ({
  id,
  name: "",
  interval,
  time: "00:00",
  intervalAgainst: "",
  templateId: type === "INVOICE_FOLLOWUP" ? INVOICE_FOLLOWUP_TEMPLATE_ID : "",
  subject: type === "INVOICE_FOLLOWUP" ? INVOICE_SUBJECT : "",
  logoUrls: [],
});

// ── Main component ────────────────────────────────────────────────────────────
const EmailFollowupTab: React.FC<Props> = ({
  followupType,
  followupData,
  setFollowupData,
  fileColumns,
  disabled = false,
}) => {
  const [previewEntryId, setPreviewEntryId] = useState<number | null>(null);
  const [templateHtml, setTemplateHtml] = useState<string | null>(null);
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);

  const openPreview = async (entryId: number) => {
    const entry = followupData.entries.find((e) => e.id === entryId);
    setPreviewEntryId(entryId);
    setTemplateHtml(null);
    setTemplateError(null);
    if (!entry?.templateId) return;
    setTemplateLoading(true);
    try {
      const res = await fetch(
        `https://stage-apiplatform.finkraftai.com/api/misc/sendgrid/template/${entry.templateId}`,
        {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("token"),
            workspaceId: localStorage.getItem("currentWorkspace") || "",
          },
        },
      );
      if (!res.ok) throw new Error(`Request failed with status ${res.status}`);
      const data = await res.json();
      const html =
        data?.versions?.[0]?.html_content ??
        data?.html_content ??
        data?.html ??
        data?.body ??
        null;
      if (!html) throw new Error("No HTML content found in template response.");
      const compiled = Handlebars.compile(html)({
        partner1_logo: entry.logoUrls[0] ?? "",
        partner2_logo: entry.logoUrls[1] ?? "",
      });
      setTemplateHtml(compiled);
    } catch (err) {
      setTemplateError(err instanceof Error ? err.message : "Unknown error");
    } finally {
      setTemplateLoading(false);
    }
  };

  const updateEntryFields = (id: number, fields: Partial<FollowupEntry>) => {
    setFollowupData({
      ...followupData,
      entries: followupData.entries.map((e) =>
        e.id === id ? { ...e, ...fields } : e,
      ),
    });
  };

  const updateEntry = (
    id: number,
    field: keyof FollowupEntry,
    value: string,
  ) => {
    setFollowupData({
      ...followupData,
      entries: followupData.entries.map((e) =>
        e.id === id ? { ...e, [field]: value } : e,
      ),
    });
  };

  const addLogoUrl = (id: number) => {
    setFollowupData({
      ...followupData,
      entries: followupData.entries.map((e) =>
        e.id === id ? { ...e, logoUrls: [...e.logoUrls, ""] } : e,
      ),
    });
  };

  const updateLogoUrl = (id: number, index: number, value: string) => {
    setFollowupData({
      ...followupData,
      entries: followupData.entries.map((e) => {
        if (e.id !== id) return e;
        const logoUrls = [...e.logoUrls];
        logoUrls[index] = value;
        return { ...e, logoUrls };
      }),
    });
  };

  const removeLogo = (id: number, index: number) => {
    setFollowupData({
      ...followupData,
      entries: followupData.entries.map((e) => {
        if (e.id !== id) return e;
        return { ...e, logoUrls: e.logoUrls.filter((_, i) => i !== index) };
      }),
    });
  };

 const addEntry = () => {
  const newId = Date.now();
  const nextInterval = followupType === "INVOICE_FOLLOWUP"
    ? String(followupData.entries.length + 1)
    : "0";  // ← "0" not "1" for GST
  setFollowupData({
    ...followupData,
    entries: [...followupData.entries, emptyEntry(newId, followupType, nextInterval)],
  });
};

  const removeEntry = (id: number) => {
    setFollowupData({
      ...followupData,
      entries: followupData.entries.filter((e) => e.id !== id),
    });
  };

  return (
    <div style={{ marginTop: 8 }}>
      <div
        style={{
          borderLeft: "3px solid #0a8394",
          paddingLeft: 12,
          marginBottom: 20,
        }}
      >
        <Typography variant="h5" weight={600}>
          Scheduler
        </Typography>
      </div>

      {/* Email Configuration card — shared fields */}
      {followupType && (
        <div
          style={{
            border: "1px solid #e8e8e8",
            borderRadius: 12,
            padding: "20px 24px",
            background: "#fafafa",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            marginBottom: 24,
          }}
        >
          {followupType === "INVOICE_FOLLOWUP" && (
            <>
              {/* Bulk Email Key */}
              <div style={{ marginBottom: 20 }}>
                <Typography variant="xs" style={{ marginBottom: 6 }}>
                  Bulk Email Key <span style={{ color: "#ff4d4f" }}>*</span>
                </Typography>
                <Select
                  showSearch
                  allowClear
                  value={followupData.bulkEmailKey || undefined}
                  placeholder={
                    fileColumns.length > 0
                      ? "Select field"
                      : "Select source first to see columns"
                  }
                  style={{ width: "100%", height: 38 }}
                  disabled={disabled || fileColumns.length === 0}
                  options={Array.from(
                    new Set([
                      ...(followupData.bulkEmailKey ? [followupData.bulkEmailKey] : []),
                      ...fileColumns,
                    ])
                  ).map((col) => ({ label: col, value: col }))}
                  onChange={(val) =>
                    setFollowupData({ ...followupData, bulkEmailKey: val ?? "" })
                  }
                />
              </div>

              {/* Attachment Keys */}
              <div style={{ marginBottom: 20 }}>
                <Typography variant="xs" style={{ marginBottom: 6 }}>
                  Attachment Keys <span style={{ color: "#ff4d4f" }}>*</span>
                </Typography>
                <Select
                  mode="multiple"
                  showSearch
                  allowClear
                  value={followupData.attachmentKeys}
                  placeholder={
                    fileColumns.length > 0
                      ? "Select one or more fields"
                      : "Select source first to see columns"
                  }
                  style={{ width: "100%" }}
                  disabled={disabled || fileColumns.length === 0}
                  options={Array.from(
                    new Set([...followupData.attachmentKeys, ...fileColumns])
                  ).map((col) => ({
                    label: followupData.attachmentKeyLabels?.[col] ?? col,
                    value: col,
                  }))}
                  onChange={(val) => {
                    const updatedLabels: Record<string, string> = {};
                    val.forEach((key: string) => {
                      updatedLabels[key] = followupData.attachmentKeyLabels?.[key] ?? key;
                    });
                    setFollowupData({ ...followupData, attachmentKeys: val, attachmentKeyLabels: updatedLabels });
                  }}
                />

                {followupData.attachmentKeys.length > 0 && (
                  <div
                    style={{
                      marginTop: 12,
                      background: "#fff",
                      border: "1px solid #e8f4f6",
                      borderRadius: 8,
                      padding: "14px 16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 10,
                      }}
                    >
                      <div
                        style={{
                          width: 3,
                          height: 14,
                          background: "#0a8394",
                          borderRadius: 2,
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant="xs"
                        weight={600}
                        style={{ color: "#262626" }}
                      >
                        Customize Header Names
                      </Typography>
                      <Typography variant="xs" style={{ color: "#8c8c8c" }}>
                        — shown to recipient in the email
                      </Typography>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "220px 32px 1fr",
                        alignItems: "center",
                        gap: 8,
                        marginBottom: 8,
                        paddingBottom: 8,
                        borderBottom: "1px solid #e8e8e8",
                      }}
                    >
                      <Typography
                        variant="xs"
                        style={{ color: "#8c8c8c", fontWeight: 500 }}
                      >
                        CSV Column
                      </Typography>
                      <div />
                      <Typography
                        variant="xs"
                        style={{ color: "#8c8c8c", fontWeight: 500 }}
                      >
                        Display Name in Email
                      </Typography>
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        maxHeight: 260,
                        overflowY: "auto",
                      }}
                    >
                      {followupData.attachmentKeys.map((key) => (
                        <div
                          key={key}
                          style={{
                            display: "grid",
                            gridTemplateColumns: "220px 32px 1fr",
                            alignItems: "center",
                            gap: 8,
                          }}
                        >
                          <Tag
                            style={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              background: "#e6f7f9",
                              borderColor: "#b5e3e8",
                              color: "#0a8394",
                              fontWeight: 500,
                              fontSize: 12,
                              padding: "4px 10px",
                              margin: 0,
                            }}
                          >
                            {key}
                          </Tag>
                          <span
                            style={{
                              color: "#0a8394",
                              fontSize: 14,
                              textAlign: "center",
                            }}
                          >
                            →
                          </span>
                          <Input
                            value={
                              followupData.attachmentKeyLabels?.[key] ?? key
                            }
                            placeholder="Custom display name"
                            style={{ height: 34, borderRadius: 6 }}
                            disabled={disabled}
                            onChange={(e) =>
                              setFollowupData({
                                ...followupData,
                                attachmentKeyLabels: {
                                  ...followupData.attachmentKeyLabels,
                                  [key]: e.target.value,
                                },
                              })
                            }
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

         {/* From Name */}
          <div style={{ marginBottom: 16 }}>
            <Typography variant="xs" style={{ marginBottom: 3 }}>
              From Name <span style={{ color: "#ff4d4f" }}>*</span>
            </Typography>
            <Input
              value={followupData.fromName}
              placeholder="ie: Finkraft Support"
              disabled={disabled}
              onChange={(e) => setFollowupData({ ...followupData, fromName: e.target.value })}
              style={{ height: 38 }}
            />
          </div>

          {/* From & CC */}
          <div style={{ display: "flex", gap: 16 }}>
            <div style={{ flex: 1 }}>
              <Typography variant="xs" style={{ marginBottom: 3 }}>
                From <span style={{ color: "#ff4d4f" }}>*</span>
              </Typography>
              <Input
                value={followupData.from}
                placeholder="ie: noreply@company.com"
                disabled={disabled}
                onChange={(e) =>
                  setFollowupData({ ...followupData, from: e.target.value })
                }
                style={{ height: 38 }}
              />
            </div>
            <div style={{ flex: 1 }}>
              <Typography variant="xs" style={{ marginBottom: 3 }}>
                CC
              </Typography>
              <Input
                value={followupData.cc}
                placeholder="ie: manager@company.com"
                disabled={disabled}
                onChange={(e) =>
                  setFollowupData({ ...followupData, cc: e.target.value })
                }
                style={{ height: 38 }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Followup entries */}
      {followupType && (
        <>
          {followupData.entries.map((entry, index) => (
            <div
              key={entry.id}
              style={{
                border: "1px solid #f0f0f0",
                borderRadius: 8,
                padding: "16px 20px",
                marginBottom: 16,
                background: "#fafafa",
              }}
            >
              {/* ── Scheduling row ── */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                  marginBottom: 16,
                }}
              >
                <Typography
                  variant="xs"
                  weight={600}
                  style={{ whiteSpace: "nowrap" }}
                >
                  Followup {index + 1} after
                </Typography>

                <InputNumber
                  min={0}
                  value={parseInt(entry.interval) || 0}
                  disabled={disabled}
                  onChange={(val) =>
                    updateEntry(entry.id, "interval", String(val ?? 0))
                  }
                  style={{ width: 70 }}
                />

                <Typography
                  variant="xs"
                  style={{ whiteSpace: "nowrap", color: "#595959" }}
                >
                  days at time
                </Typography>

                <Input
                  type="time"
                  value={entry.time}
                  disabled={disabled}
                  onChange={(e) =>
                    updateEntry(entry.id, "time", e.target.value)
                  }
                  style={{ width: 120, height: 32 }}
                />

                {followupType === "GST_TRANSMISSION" && (
                  <>
                    <Typography
                      variant="xs"
                      style={{ whiteSpace: "nowrap", color: "#595959" }}
                    >
                      of
                    </Typography>
                    <Select
                      value={entry.intervalAgainst || undefined}
                      placeholder="Select date"
                      style={{ width: 320 }}
                      disabled={disabled}
                      onChange={(val) => {
                        updateEntryFields(entry.id, {
                          intervalAgainst: val,
                          name: INTERVAL_TO_NAME[val] ?? "",
                          templateId: INTERVAL_TO_TEMPLATE_ID[val] ?? "",
                          subject: GST_SUBJECT,
                        });
                      }}
                      options={INTERVAL_AGAINST_OPTIONS}
                    />
                  </>
                )}

                <div style={{ flex: 1 }} />
                {followupData.entries.length > 1 && (
                  <Button
                    type="text"
                    danger
                    icon={<DeleteFilled />}
                    disabled={disabled}
                    onClick={() => removeEntry(entry.id)}
                  />
                )}
              </div>

              {/* ── Name (GST only, auto-populated) ── */}
              {followupType === "GST_TRANSMISSION" && (
                <div style={{ marginBottom: 12 }}>
                  <Typography variant="xs" style={{ marginBottom: 3 }}>
                    Name
                  </Typography>
                  <Input
                    value={entry.name}
                    disabled
                    placeholder="Auto-filled on interval selection"
                    style={{ height: 38 }}
                  />
                </div>
              )}

              {/* ── Subject ── */}
              <div style={{ marginBottom: 12 }}>
                <Typography variant="xs" style={{ marginBottom: 3 }}>
                  Subject <span style={{ color: "#ff4d4f" }}>*</span>
                </Typography>
                <Input
                  value={entry.subject}
                  disabled={disabled || followupType === "GST_TRANSMISSION"}
                  placeholder={
                    followupType === "GST_TRANSMISSION"
                      ? "Auto-filled on interval selection"
                      : "Enter email subject"
                  }
                  onChange={(e) =>
                    followupType === "INVOICE_FOLLOWUP" &&
                    updateEntry(entry.id, "subject", e.target.value)
                  }
                  style={{ height: 38 }}
                />
              </div>

              {/* ── Logo URLs ── */}
              <div style={{ marginBottom: 12 }}>
                <Typography variant="xs" style={{ marginBottom: 6 }}>
                  Logo URLs <span style={{ color: "#ff4d4f" }}>*</span>
                </Typography>

                {entry.logoUrls.map((url, i) => (
                  <div
                    key={i}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      marginBottom: 8,
                    }}
                  >
                    {url && (
                      <img
                        src={url}
                        alt={`logo-${i}`}
                        style={{
                          width: 28,
                          height: 28,
                          objectFit: "contain",
                          borderRadius: 4,
                          flexShrink: 0,
                        }}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = "none";
                        }}
                      />
                    )}
                    <Input
                      value={url}
                      placeholder={`Logo ${i + 1} URL`}
                      disabled={disabled}
                      onChange={(e) =>
                        updateLogoUrl(entry.id, i, e.target.value)
                      }
                      style={{ height: 38, flex: 1 }}
                    />
                    <Button
                      type="text"
                      danger
                      size="small"
                      icon={<CloseCircleFilled style={{ fontSize: 14 }} />}
                      disabled={disabled}
                      onClick={() => removeLogo(entry.id, i)}
                      style={{
                        padding: 0,
                        height: "auto",
                        lineHeight: 1,
                        flexShrink: 0,
                      }}
                    />
                  </div>
                ))}

                <Button
                  icon={<PlusCircleFilled />}
                  disabled={disabled}
                  onClick={() => addLogoUrl(entry.id)}
                  style={{
                    height: 38,
                    borderStyle: "dashed",
                    borderColor: "#0a8394",
                    color: "#0a8394",
                  }}
                >
                  Add Logo URL
                </Button>
              </div>

              {/* ── Template ID ── */}
              <div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: 3,
                  }}
                >
                  <Typography variant="xs">
                    Template ID <span style={{ color: "#ff4d4f" }}>*</span>
                  </Typography>
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    disabled={disabled}
                    onClick={() => openPreview(entry.id)}
                    style={{
                      color: "#0a8394",
                      padding: 0,
                      height: "auto",
                      fontSize: 12,
                    }}
                  >
                    Preview
                  </Button>
                </div>
                <Input
                  value={entry.templateId}
                  disabled={disabled || followupType === "GST_TRANSMISSION"}
                  placeholder={
                    followupType === "GST_TRANSMISSION"
                      ? "Auto-filled on interval selection"
                      : "Enter SendGrid template ID"
                  }
                  onChange={(e) =>
                    followupType === "INVOICE_FOLLOWUP" &&
                    updateEntry(entry.id, "templateId", e.target.value)
                  }
                  style={{ height: 38 }}
                />
              </div>
            </div>
          ))}

          <Button
            icon={<PlusCircleFilled />}
            onClick={addEntry}
            disabled={disabled}
            className="add-flow-button"
            style={{ marginTop: 4 }}
          >
            Add followup
          </Button>
        </>
      )}

      {/* ── Preview Modal ── */}
      <Modal
        open={previewEntryId !== null}
        onCancel={() => setPreviewEntryId(null)}
        onOk={() => setPreviewEntryId(null)}
        okText="Looks Good"
        cancelText="Cancel"
        okButtonProps={{
          style: { background: "#0a8394", borderColor: "#0a8394" },
        }}
        width={900}
        title={null}
        styles={{ body: { padding: 0, maxHeight: "75vh", overflowY: "auto" } }}
        centered
      >
        {templateLoading && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              minHeight: 360,
              gap: 12,
            }}
          >
            <Spin size="large" />
            <span style={{ fontSize: 13, color: "#8C8C8C" }}>
              Loading template…
            </span>
          </div>
        )}

        {!templateLoading && templateError && (
          <div style={{ padding: 24 }}>
            <Alert
              type="error"
              icon={<ExclamationCircleOutlined />}
              showIcon
              message="Failed to load template"
              description={templateError}
              style={{ borderRadius: 8 }}
            />
          </div>
        )}

        {!templateLoading && !templateError && templateHtml && (
          <iframe
            srcDoc={templateHtml}
            title="Email template preview"
            sandbox="allow-same-origin"
            style={{
              width: "100%",
              minHeight: 500,
              border: "none",
              display: "block",
            }}
          />
        )}

        {!templateLoading && !templateError && !templateHtml && (
          <div
            style={{
              padding: "20px 24px",
              background: "#FFF7E6",
              borderRadius: 8,
              margin: 24,
            }}
          >
            <p style={{ margin: 0, fontSize: 13, color: "#874D00" }}>
              <strong>No template ID set.</strong> Set a template ID first, then
              click Preview.
            </p>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default EmailFollowupTab;





